/* eslint-disable max-params */
import { lazy as ReactLazy, useCallback, useRef, useState } from 'react';
import { useEffectOnceWhen } from 'rooks';

import { singletonTTIPromises, DELAYS } from '../../../utils/tti';
import { loadRemoteModule as loadModule } from '../helpers/remote';
import type { TLazyRemote, TRemote } from '../types';
//@ts-ignore
import { noop } from '@mindtickle/utils';

const fallbackModule = {
  default: () => null,
};

// eslint-disable-next-line max-params
function useRemoteInitialisation(
  remote: TRemote,
  isManifestReady: boolean,
  lazy: TLazyRemote['lazy'],
  maxDelay: DELAYS,
  shouldFailSilently = false,
  moduleEvaluationTimingCallback = noop
) {
  const [isReadyForRendering, setReadyForRendering] = useState(false);
  const [downloadCompleted, setDownloadCompleted] = useState(false);

  const moduleDetailsRef = useRef<
    undefined | { module: Promise<any>; Component: React.ComponentType }
  >(undefined);

  const getModuleDetails = useCallback(() => {
    const module = loadModule(remote.remoteName, remote.moduleName, moduleEvaluationTimingCallback);
    return {
      module,
      Component: ReactLazy(() =>
        module
          .then(_module => {
            setDownloadCompleted(true);
            return _module;
          })
          .catch(error => {
            if (shouldFailSilently) {
              return fallbackModule;
            }
            throw new Error(error);
          })
      ),
    };
  }, [remote.moduleName, remote.remoteName, shouldFailSilently, moduleEvaluationTimingCallback]);

  const updateModuleDetails = useCallback(() => {
    if (!isReadyForRendering) {
      moduleDetailsRef.current = getModuleDetails();
      setReadyForRendering(true);
    }
  }, [isReadyForRendering, getModuleDetails]);

  useEffectOnceWhen(() => {
    const ttiPromise = singletonTTIPromises[maxDelay] || singletonTTIPromises[DELAYS.L];
    lazy ? ttiPromise.then(updateModuleDetails) : setTimeout(updateModuleDetails, 1);
  }, isManifestReady);
  return {
    isReadyForRendering,
    downloadCompleted,
    moduleDetails: moduleDetailsRef.current,
  };
}

export default useRemoteInitialisation;
